import { Grid, GridItem, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useGetContractQuery } from "../../../autogen/bff-api";
import { EntityViewSkeleton } from "../../../common/EntityViewSkeleton";
import { isFetchBaseQueryError } from "../../../common/errors/isFetchBaseQueryError";
import { useApiError } from "../../../common/errors/useApiError";
import { Layout } from "../../../common/layout/Layout";
import { ContractState } from "../../../common/redux/reducers/contractReducer";
import { ContractsTab, useQueryParams } from "../../../common/useQueryParams";
import { CONTENT_WIDTH } from "../../../config";
import { requireBooleanEnvVar } from "../../../config";
import { requireCounterparty, requireDataFields } from "../typeguards";
import { ContractMeta } from "./ContractMeta";
import { Details } from "./details/Details";
import { Documents } from "./documents/Documents";
import { Notifications } from "./notifications/Notifications";
import { Sharing } from "./sharing/Sharing";
import { Tasks } from "./tasks/Tasks";
import { ContractDialog } from "./dialog/ContractDialog";
import { FencyChatWithContract } from "./ai-chat/FencyChatWithContract";

const indexToQueryValue = (index: number): ContractsTab => {
  if (index === 0) return "info";
  else if (index === 1) return "documents";
  else if (index === 2) return "reminders";
  else if (index === 3) return "tasks";
  else if (index === 4) return "sharing";
  else if (index === 5) return "dialog";
  else if (index === 6) return "ai-chat";
  else throw Error("Invalid index");
};

const tabToIndex = (tab: ContractsTab): number => {
  switch (tab) {
    case "info":
      return 0;
    case "documents":
      return 1;
    case "reminders":
      return 2;
    case "tasks":
      return 3;
    case "sharing":
      return 4;
    case "dialog":
      return 5;
    case "ai-chat":
      return 6;
  }
};

export const ContractPage = ({ embedded = false }: { embedded?: boolean }) => {
  const { contractId } = useParams();
  const [tabIndex, setTabIndex] = useState(0);
  const params = useQueryParams();
  const { t } = useTranslation();
  const { data: contract, error, isLoading, refetch } = useGetContractQuery(
    contractId ? { contractId } : skipToken
  );
  const fencyDocumentChatEnabled = requireBooleanEnvVar("VITE_ENABLE_FENCY_DOCUMENT_CHAT");
  const displayer = useApiError();

  useEffect(() => {
    const tab = params.getContractsTab();
    setTabIndex(tabToIndex(tab));
  }, [params]);

  if (error) {
    if (isFetchBaseQueryError(error) && error.status === 403) return displayer.showNoAccess();
    else return displayer.show(error);
  }

  const endDate = contract ? requireDataFields(contract as ContractState["contract"]).endDate : undefined;

  const body = (
    <Grid
      width="full"
      gridTemplateColumns={"3fr 4fr 200px"}
      gridTemplateRows="minmax(50px,1fr) auto auto auto minmax(0,1fr)"
      templateAreas={`
              "title title edit"
              "createdMeta createdMeta edit"
              "deadline deadline deadline"
              "content content content"
              `}
      px="1"
    >
      {isLoading && <EntityViewSkeleton />}
      {contract && contract.id === contractId && (
        <>
          <ContractMeta
            id={contract.id}
            title={contract.title}
            counterparty={requireCounterparty(contract)}
            status={contract.status ?? undefined}
            expirationDate={endDate?.date ?? null}
            canEdit={contract.canEditEverything}
            template={contract.template ?? null}
          />
          <GridItem area={"content"} display={"flex"} flexDirection={"column"} minW="0">
            <Tabs
              isLazy={true}
              mt="25px"
              width={"100%"}
              index={tabIndex}
              onChange={(index) => {
                setTabIndex(index);
                params.setSelectedContractsTab(indexToQueryValue(index));
              }}
              variant="line"
              colorScheme="teal"
            >
              <TabList overflowX="auto" overflowY="hidden">
                {contract.canViewDataFields && <Tab whiteSpace="nowrap">{t("Details")}</Tab>}
                {contract.canViewDocuments && <Tab whiteSpace="nowrap">{t("Documents")}</Tab>}
                {contract.canViewNotifications && <Tab whiteSpace="nowrap">{t("Reminders")}</Tab>}
                {contract.canViewTasks && <Tab whiteSpace="nowrap">{t("Tasks")}</Tab>}
                {contract.canViewParticipants && <Tab whiteSpace="nowrap">{t("Sharing")}</Tab>}
                <Tab whiteSpace="nowrap">{t("Dialog with counterparty")}</Tab>
                {contract.canViewDocuments && fencyDocumentChatEnabled && (
                  <Tab whiteSpace="nowrap">{t("AI Chat")}</Tab>
                )}
              </TabList>
              <TabPanels>
                {contract.canViewDataFields && (
                  <TabPanel p="0" pt="8">
                    <Details data={contract as ContractState["contract"]} />
                  </TabPanel>
                )}
                {contract.canViewDocuments && (
                  <TabPanel p="0" pt="8">
                    <Documents contract={contract} canEdit={contract.canEditEverything} />
                  </TabPanel>
                )}
                {contract.canViewNotifications && (
                  <TabPanel p="0" pt="8">
                    <Notifications data={contract} />
                  </TabPanel>
                )}
                {contract.canViewTasks && (
                  <TabPanel p="0" pt="8">
                    <Tasks contract={contract} />
                  </TabPanel>
                )}
                {contract.canViewParticipants && (
                  <TabPanel p="0" pt="8">
                    <Sharing contract={contract} />
                  </TabPanel>
                )}
                <TabPanel p="0" pt="8">
                  <ContractDialog
                    contractId={contract.id}
                    documents={contract.documentFields?.documents ?? []}
                    canManageContract={contract.canEditEverything}
                    showOriginCommunication={contract.dataFields?.originSourcingEvent?.type === "SourcingEventV2"}
                  />
                </TabPanel>
                {contract.canViewDocuments && fencyDocumentChatEnabled && (
                  <TabPanel
                    p="0"
                    pt="8"
                    height="calc(100vh - 250px)"
                    minH="700px"
                    display="flex"
                    flexDirection="column"
                    overflow="hidden"
                  >
                    <FencyChatWithContract
                      contract={contract}
                      refetchContract={() => {
                        void refetch();
                      }}
                    />
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </GridItem>
        </>
      )}
    </Grid>
  );

  if (embedded) {
    return body;
  }

  return (
    <Layout>
      <Grid
        height="full"
        gap={1}
        gridTemplateColumns={`1fr minmax(0, ${CONTENT_WIDTH}) 1fr`}
        gridTemplateRows={"1fr"}
        templateAreas={`
                "leftContent content rightContent"
                `}
      >
        <GridItem area={"content"}>{body}</GridItem>
      </Grid>
    </Layout>
  );
};
